/**
 * WinBackPanel — admin-only visibility into the win-back automation (iter51).
 * Surfaces: pending / sent / cancelled counts, cancellation reason breakdown,
 * total permanent opt-outs, and recent recovery examples.
 */
import React, { useEffect, useState } from 'react';
import {
  Heart, RefreshCw, Loader2, Send, Ban, Clock, XOctagon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const Tile = ({ icon: Icon, label, value, accent, sublabel, testId }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold ${accent || 'text-slate-100'} mt-1`} data-testid={testId}>
            {value}
          </p>
          {sublabel && <p className="text-[10px] text-slate-500 mt-0.5">{sublabel}</p>}
        </div>
        <Icon className={`h-5 w-5 ${accent || 'text-slate-500'}`} />
      </div>
    </CardContent>
  </Card>
);

const REASON_LABEL = {
  resubscribed: 'User came back on their own',
  winback_optout: 'User opted out of win-backs',
  user_deleted: 'Account deleted',
  deletion_pending: 'Account in deletion grace',
  already_sent: 'Category already fired once',
  send_failed: 'SendGrid rejected the send',
};

const WinBackPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/metrics/winbacks');
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load win-back metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <Card className="bg-[#1A2440] border-[#2A3654]" data-testid="winback-panel-loading">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Heart className="h-5 w-5" /> Win-back automation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            <Skeleton className="h-20" /><Skeleton className="h-20" />
            <Skeleton className="h-20" /><Skeleton className="h-20" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const c = data?.counts || {};
  const total = (c.pending || 0) + (c.sent || 0) + (c.cancelled || 0) + (c.failed || 0);
  const recoveryRate = c.sent && total ? Math.round((c.sent / total) * 100) : 0;

  return (
    <Card className="bg-[#1A2440] border-[#2A3654]" data-testid="winback-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-slate-100">
          <Heart className="h-5 w-5 text-rose-400" /> Win-back automation
        </CardTitle>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="winback-refresh-btn"
          className="text-slate-400 hover:text-white hover:bg-[#2A3654]"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Tile
            icon={Clock}
            label="Pending"
            value={c.pending ?? 0}
            accent="text-amber-400"
            sublabel={`${data?.scheduled_today || 0} due in next 24h`}
            testId="winback-pending"
          />
          <Tile
            icon={Send}
            label="Sent"
            value={c.sent ?? 0}
            accent="text-emerald-400"
            sublabel="Users we tried to win back"
            testId="winback-sent"
          />
          <Tile
            icon={Ban}
            label="Cancelled"
            value={c.cancelled ?? 0}
            accent="text-slate-400"
            sublabel="Prevented before send"
            testId="winback-cancelled"
          />
          <Tile
            icon={XOctagon}
            label="Permanent opt-out"
            value={data?.total_optedout ?? 0}
            accent="text-rose-400"
            sublabel="Users who said 'never again'"
            testId="winback-optedout"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-semibold text-slate-200 mb-2 flex items-center gap-2">
              Cancellation breakdown
              <Badge variant="outline" className="text-[10px]">{c.cancelled || 0} total</Badge>
            </h4>
            {Object.keys(data?.cancelled_reasons || {}).length === 0 ? (
              <p className="text-xs text-slate-500 italic">No cancellations yet.</p>
            ) : (
              <div className="space-y-1.5">
                {Object.entries(data.cancelled_reasons).map(([reason, count]) => (
                  <div
                    key={reason}
                    className="flex items-center justify-between px-3 py-1.5 bg-[#0F1730] rounded border border-[#1F2A4D]"
                    data-testid={`winback-reason-${reason}`}
                  >
                    <span className="text-xs text-slate-300">
                      {REASON_LABEL[reason] || reason}
                    </span>
                    <span className="text-xs font-mono text-slate-100">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-200 mb-2">
              Recent recovery attempts
            </h4>
            {(data?.recovery_examples || []).length === 0 ? (
              <p className="text-xs text-slate-500 italic">No sent win-backs yet — check back after users have opted out.</p>
            ) : (
              <div className="space-y-1.5">
                {data.recovery_examples.map((ex, idx) => (
                  <div
                    key={ex.email + idx}
                    className="flex items-center justify-between px-3 py-1.5 bg-[#0F1730] rounded border border-[#1F2A4D]"
                  >
                    <div className="min-w-0">
                      <p className="text-xs text-slate-300 truncate">{ex.email}</p>
                      <p className="text-[10px] text-slate-500">
                        {ex.category.replace('_', ' ')} · {new Date(ex.sent_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {c.sent > 0 && (
          <div className="mt-4 p-3 bg-emerald-950/20 border border-emerald-800/30 rounded flex items-center gap-2">
            <Heart className="h-4 w-4 text-emerald-400" />
            <p className="text-xs text-emerald-200">
              <strong>{recoveryRate}%</strong> of scheduled win-backs actually went out — the rest were prevented by resubscribes, opt-outs, or account deletions.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WinBackPanel;
